<script>
    import { onMount, beforeUpdate } from "svelte";
    import { Chart, registerables } from "chart.js";
    import annotationPlugin from "chartjs-plugin-annotation";
    import { theme } from "$stores/themeStore";
    import { get } from 'svelte/store';
    import { studyProperties } from '$stores/configStore';

    export let data = [];

    let canvas;
    let chart;
    let chartBorderColor = "rgba(75, 192, 192, 1)";
    let chartBackgroundColor = "rgba(75, 192, 192, 0.2)";

    // Number of bars to display at a time
    const visibleBars = 1344;
    
    // Dynamic target per hour based on study interval and CGM compliance threshold
    $: intervalMinutes = $studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5;
    $: expectedPerHour = Math.round(60 / Math.max(1, intervalMinutes));
    $: cgmComplianceThreshold = $studyProperties?.glucoseParameters?.cgmCompliance?.complianceThreshold ?? 80;
    $: targetValue = Math.round(expectedPerHour * (cgmComplianceThreshold / 100));

    // Calculate the width of the canvas based on the number of data items
    const barWidth = 8; // Width of each bar in pixels
    const canvasWidth = data.length * barWidth; // Total canvas width

    const lightModeOptions = {
        responsive: false,
        maintainAspectRatio: false,
        scales: {
            x: {
                title: {
                    display: false,
                    text: "Date",
                    color: "black",
                },
                ticks: {
                    color: "black",
                },
                min: 0, // Start at the first data point
                max: visibleBars - 1, // Show only 168 bars at a time
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "black",
                },
                position: "left",
                ticks: {
                    color: "black",
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            canvas: {
                responsive: true,
                maintainAspectRatio: false,
                resizeDelay: 1000,
            },
            annotation: {
                annotations: {
                    line1: {
                        type: "line",
                        yMin: 0,
                        yMax: 0,
                        borderColor: "black",
                        borderWidth: 0.5,
                        label: {
                            content: '',
                            display: true,
                            position: "end",
                            backgroundColor: "rgba(0,0,0,0.7)",
                            font: {
                                size: 10
                            }
                        }
                    },
                },
            },
        },
    };

    const darkModeOptions = {
        responsive: false,
        maintainAspectRatio: false,
        scales: {
            x: {
                title: {
                    display: false,
                    text: "Date",
                    color: "white",
                },
                ticks: {
                    color: "white",
                },
                min: 0, // Start at the first data point
                max: visibleBars - 1, // Show only 168 bars at a time
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "white",
                },
                position: "left",
                ticks: {
                    color: "white",
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            canvas: {
                responsive: true,
                maintainAspectRatio: false,
                resizeDelay: 1000,
            },
            annotation: {
                annotations: {
                    line1: {
                        type: "line",
                        yMin: 0,
                        yMax: 0,
                        borderColor: "rgba(110, 210, 170, 0.8)",
                        borderWidth: 0.5,
                        label: {
                            content: '',
                            display: true,
                            position: "end",
                            backgroundColor: "rgba(33,33,33,0.7)",
                            color: "white",
                            font: {
                                size: 10
                            }
                        }
                    },
                },
            },
        },
    };

    onMount(() => {
        // Validate data
        if (!Array.isArray(data) || data.length === 0) {
            console.error("BarChart received invalid data: ", data.length);
            return;
        }

        Chart.register(...registerables, annotationPlugin);
        createChart();
    });

    beforeUpdate(() => {
        if (chart) {
            chart.destroy();
            chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.2)" : "rgba(190, 24, 123, 0.2)";
            chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(190, 24, 123, 1)";   
            createChart();
        }
    });

    $: if (chart) {
        chart.destroy();
        chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.2)" : "rgba(190, 24, 123, 0.2)";
            chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(190, 24, 123, 1)";   
        createChart();
    }

    function createChart() {
        const currentTheme = get(theme); // Get the current theme
        const options = currentTheme === "dark" ? JSON.parse(JSON.stringify(darkModeOptions)) : JSON.parse(JSON.stringify(lightModeOptions));
        // Inject dynamic annotation target from study settings
        if (options?.plugins?.annotation?.annotations?.line1) {
            options.plugins.annotation.annotations.line1.yMin = targetValue;
            options.plugins.annotation.annotations.line1.yMax = targetValue;
            options.plugins.annotation.annotations.line1.label.content = `Target: ${targetValue}`;
        }

        const ctx = canvas.getContext("2d");

        const formattedLabels = data.map((item) => {
            const date = new Date(item.hour);
            const options = { month: 'numeric', day: 'numeric', weekday: 'short', hour: 'numeric', hour12: true };
            return date.toLocaleString('en-US', options); // Formats as "Mon 5PM"
        });

        // Use a consistent color for most bars, with only Sunday being different
        const mainColor = currentTheme === "dark" ? 
            "rgba(13, 148, 136, 0.7)" : 
            "rgba(190, 24, 123, 0.7)";
            
        const sundayColor = currentTheme === "dark" ? 
            "rgba(13, 148, 236, 0.7)" : 
            "rgba(190, 24, 223, 0.7)";

        const backgroundColors = data.map((item) => {
            const date = new Date(item.hour);
            // Only Sundays get a different color
            return date.getDay() === 0 ? sundayColor : mainColor;
        });

        chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels: formattedLabels,
                datasets: [
                    {
                        label: "Count",
                        data: data.map((item) => item.count),
                        backgroundColor: backgroundColors, // Apply the alternating colors
                        borderColor: backgroundColors,
                        borderWidth: 1,
                    },
                ],
            },
            options: options,
        });
    }

    theme.subscribe(() => {
        if (chart) {
            chart.destroy();
            chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.2)" : "rgba(190, 24, 123, 0.2)";
            chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(190, 24, 123, 1)";   
            createChart();
        }
    });
</script>

<!-- <canvas bind:this={canvas}></canvas> -->

<div style="overflow-x: auto; width: 100%; height: 250px; scrollbar-width: none; -ms-overflow-style: none;">
    <style>
        div::-webkit-scrollbar {
            display: none;
        }
    </style>
    <canvas bind:this={canvas} width={canvasWidth} height="250"></canvas>
</div>
